// Launcher (SM-756): generate a single-use, 60-second launch code for a
// user and hand it to Smart Market's callback.
//
// The code is shown ONCE. It lives only in this component's state: it is
// never put in the TanStack query / mutation cache, localStorage,
// sessionStorage, the console's URL or the console log, and it is
// dropped when the operator navigates away (unmount), picks another user
// or generates the next one. The code list shows the server's hint only.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { UserPicker } from "@/components/domain/UserPicker";
import { StatusBadge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { SectionCard } from "@/components/ui/Card";
import { ErrorBanner } from "@/components/ui/ErrorBanner";
import { Field, Select, TextInput } from "@/components/ui/Field";
import { CopyButton, Empty, Mono, PageHeader, ReadOnlyHint, TableWrap } from "@/components/ui/Misc";
import { api } from "@/lib/api";
import { fmtCountdown, fmtTime } from "@/lib/format";
import { launchOpenState } from "@/lib/launch";
import { fetchers, keys } from "@/lib/queries";
import { useCanMutate } from "@/lib/session";
import type { IssuedCode } from "@/lib/types";
import { useNow } from "@/lib/useNow";

interface LauncherSearch {
  user?: string;
}

export const Route = createFileRoute("/_app/launcher")({
  validateSearch: (s: Record<string, unknown>): LauncherSearch => ({
    user: typeof s.user === "string" && s.user ? s.user : undefined,
  }),
  component: LauncherPage,
});

function LauncherPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/launcher" });
  const userId = search.user ?? "";
  return (
    <div className="space-y-5">
      <PageHeader
        title="Launcher"
        subtitle="Issue a launch code (≥160-bit, single use, 60 s) and open Smart Market's launch callback with it."
      />
      <div className="card">
        <UserPicker value={userId} onChange={(id) => navigate({ search: { user: id || undefined } })} />
      </div>
      {/* key: a different user is a fresh component — any revealed code is dropped */}
      {userId ? <LaunchForUser key={userId} userId={userId} /> : <Empty>Choose a user to issue a launch code for.</Empty>}
      <CodeList userId={userId} />
    </div>
  );
}

function LaunchForUser({ userId }: { userId: string }) {
  const canMutate = useCanMutate();
  const qc = useQueryClient();
  const user = useQuery({ queryKey: keys.user(userId), queryFn: () => fetchers.user(userId) });
  const clients = useQuery({ queryKey: keys.clients(), queryFn: fetchers.clients });
  const client = (clients.data ?? []).find((c) => c.client_id === user.data?.client_id);
  const urls = client?.callback_urls ?? [];
  const [callback, setCallback] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // The one-time code. Component state only.
  const [issued, setIssued] = useState<IssuedCode | null>(null);

  const status = user.data?.account_status;
  const chosenUrl = callback || urls[0] || "";

  return (
    <>
      <SectionCard title="Generate launch code">
        {!canMutate && <ReadOnlyHint />}
        <ErrorBanner error={user.error} />
        {status && status !== "ACTIVE" && (
          <div className="banner warn" role="status">
            <span aria-hidden>⚠</span>
            <span>
              <b>{userId}</b> is <b>{status}</b>. The code still exchanges, answering <span className="mono">account_status {status}</span>,
              and Smart Market must refuse the session — use this to test that path.
            </span>
          </div>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            setIssued(null);
            try {
              const out = await api<IssuedCode>("/launch-codes", {
                method: "POST",
                body: { user_id: userId, client_id: user.data?.client_id ?? "", callback_url: chosenUrl, reason: reason.trim() },
              });
              setIssued(out);
              await qc.invalidateQueries({ queryKey: ["mock", "launch-codes"] });
            } catch (err) {
              setError(err);
            } finally {
              setBusy(false);
            }
          }}
        >
          <fieldset disabled={!canMutate || busy || !user.data}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Client">
                <TextInput value={user.data?.client_id ?? ""} readOnly className="mono" />
              </Field>
              <Field label="Callback URL" hint="One of the client's exact registered callback URLs.">
                {urls.length > 1 ? (
                  <Select value={chosenUrl} onChange={(e) => setCallback(e.target.value)} className="mono">
                    {urls.map((u) => (
                      <option key={u}>{u}</option>
                    ))}
                  </Select>
                ) : (
                  <TextInput value={chosenUrl} readOnly className="mono" />
                )}
              </Field>
              <Field label="Reason (optional, audited)" className="sm:col-span-2">
                <TextInput value={reason} onChange={(e) => setReason(e.target.value)} placeholder="launch from the sandbox console" />
              </Field>
            </div>
            <ErrorBanner error={error} className="mt-3" />
            <div className="mt-4 flex justify-end">
              <Button type="submit" loading={busy} disabled={!chosenUrl}>
                Generate code
              </Button>
            </div>
          </fieldset>
        </form>
      </SectionCard>

      {issued && <Reveal issued={issued} onDismiss={() => setIssued(null)} />}
    </>
  );
}

function Reveal({ issued, onDismiss }: { issued: IssuedCode; onDismiss: () => void }) {
  const now = useNow(1000);
  // The code opened from here, if any: one open per code (SM-813).
  const [opened, setOpened] = useState<IssuedCode | null>(null);
  const state = launchOpenState(issued, opened, now);
  const expired = state === "expired";
  return (
    <div className="reveal-box" data-testid="code-reveal">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-semibold text-t-1">Launch code — shown once</div>
        <span className={expired ? "pill no" : "pill warn"}>{expired ? "expired" : `expires ${fmtCountdown(issued.expires_at, now)}`}</span>
      </div>
      <p className="mb-3 text-xs text-t-3">
        Copy it now or open Smart Market directly. It is not stored by this console and disappears when you leave this
        page; the list below only ever shows its hint <span className="mono">{issued.code_hint}</span>.
      </p>
      <div className="space-y-3">
        <div>
          <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-t-3">Code</div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="reveal-secret min-w-0 flex-1">{issued.code}</div>
            <CopyButton value={issued.code} label="Copy code" />
          </div>
        </div>
        <div>
          <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-t-3">Launch URL</div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="reveal-secret min-w-0 flex-1">{issued.launch_url}</div>
            <CopyButton value={issued.launch_url} label="Copy URL" />
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        {/* noopener makes window.open return null either way, so a blocked tab cannot be told apart: name the fallback. */}
        {state === "opened" && (
          <span className="text-xs text-t-3">
            Sent to a new tab; if none appeared, use Copy URL. A code opens once; generate a new one to launch again.
          </span>
        )}
        <Button variant="ghost" onClick={onDismiss}>
          Hide code
        </Button>
        <Button
          disabled={state !== "ready"}
          onClick={() => {
            setOpened(issued);
            // Top-level navigation in a new tab, no opener, no referrer.
            window.open(issued.launch_url, "_blank", "noopener,noreferrer");
          }}
        >
          Open Smart Market
        </Button>
      </div>
    </div>
  );
}

function CodeList({ userId }: { userId: string }) {
  const codes = useQuery({
    queryKey: keys.launchCodes(userId),
    queryFn: () => fetchers.launchCodes(userId),
    refetchInterval: 5_000,
  });
  const rows = codes.data ?? [];
  return (
    <SectionCard
      title={userId ? `Launch codes for ${userId}` : "Recent launch codes"}
      actions={
        <Button variant="ghost" size="sm" onClick={() => codes.refetch()} loading={codes.isFetching}>
          Refresh
        </Button>
      }
    >
      <ErrorBanner error={codes.error} />
      {rows.length === 0 ? (
        <Empty>No launch codes yet.</Empty>
      ) : (
        <TableWrap>
          <table className="tbl" data-testid="code-list">
            <thead>
              <tr>
                <th>Hint</th>
                <th>State</th>
                <th>User</th>
                <th>Issued</th>
                <th>Expires</th>
                <th>Consumed</th>
                <th>Consumed by</th>
                <th>Rejected code</th>
                <th>Created by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.code_hint + c.issued_at}>
                  <td>
                    <Mono>{c.code_hint}</Mono>
                  </td>
                  <td>
                    <StatusBadge status={c.state} />
                  </td>
                  <td>
                    <Link to="/users/$id" params={{ id: c.user_id }} className="mono">
                      {c.user_id}
                    </Link>
                  </td>
                  <td>{fmtTime(c.issued_at)}</td>
                  <td>{fmtTime(c.expires_at)}</td>
                  <td>{fmtTime(c.consumed_at)}</td>
                  <td>
                    <Mono>{c.consumed_by_client ?? "—"}</Mono>
                  </td>
                  <td>
                    <Mono>{c.rejected_code ?? "—"}</Mono>
                  </td>
                  <td>{c.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </SectionCard>
  );
}
